/* TutorialRunner.tsx — the engine that sequences a TutorialDef.

   Owns: which step is active, running each step's onEnter (prepare the page),
   soft-gated advancement (a step with action.advanceWhen auto-advances the moment
   the user does the real thing, but the primary button is always available so
   nobody gets stuck), and marking the tutorial "seen" on completion.

   Renders the right surface per step: ConceptStage (full-screen Sofi) for
   'concept' steps, CoachOverlay (anchored spotlight) for 'coach' steps. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConceptStage } from './ConceptStage';
import { CoachOverlay } from './CoachOverlay';
import { markTutorialSeen } from './seen';
import type { TutorialDef, TutorialStep } from './types';

interface Props<Ctx> {
  def: TutorialDef<Ctx>;
  ctx: Ctx;
  open: boolean;
  /** completed = reached the end; false = exited early. */
  onClose: (completed: boolean) => void;
}

export function TutorialRunner<Ctx>({ def, ctx, open, onClose }: Props<Ctx>) {
  const { t } = useTranslation('common');
  const [activeId, setActiveId] = useState<string | null>(null);

  // Live refs so poll-based advance checks and async onEnter never read a stale ctx/step.
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const visible = (): TutorialStep<Ctx>[] => def.steps.filter((s) => !s.when || s.when(ctxRef.current));

  // Initialise / reset when opened.
  useEffect(() => {
    if (open) setActiveId(visible()[0]?.id ?? null);
    else setActiveId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, def.id]);

  const steps = visible();
  const index = steps.findIndex((s) => s.id === activeId);
  const step = index >= 0 ? steps[index] : null;

  const finish = useCallback((completed: boolean) => {
    // Any close counts as seen — closing with X is the usual "not now", and an
    // auto-opened guide must not come back on every visit. It stays re-openable by hand.
    markTutorialSeen(def.id);
    setActiveId(null);
    onClose(completed);
  }, [def.id, onClose]);

  const goTo = useCallback((i: number) => {
    const list = def.steps.filter((s) => !s.when || s.when(ctxRef.current));
    if (i < 0) return;
    const target = list[i];
    if (!target) { finish(true); return; } // past the last visible step → done
    setActiveId(target.id);
  }, [def.steps, finish]);

  const next = useCallback(() => {
    const list = def.steps.filter((s) => !s.when || s.when(ctxRef.current));
    const cur = list.findIndex((s) => s.id === activeId);
    goTo(cur + 1);
  }, [def.steps, activeId, goTo]);

  const back = useCallback(() => {
    const list = def.steps.filter((s) => !s.when || s.when(ctxRef.current));
    const cur = list.findIndex((s) => s.id === activeId);
    goTo(cur - 1);
  }, [def.steps, activeId, goTo]);

  // Run onEnter when the active step changes; record whether its action-gate was
  // already satisfied on entry (so an already-true gate doesn't instantly skip).
  const gateOnEnterRef = useRef(false);
  useEffect(() => {
    if (!step) return;
    void Promise.resolve(step.onEnter?.(ctxRef.current));
    gateOnEnterRef.current = step.action?.advanceWhen ? !!step.action.advanceWhen(ctxRef.current) : true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Poll the active step's advanceWhen; auto-advance when it flips true after entry.
  useEffect(() => {
    if (!step?.action?.advanceWhen) return;
    const id = window.setInterval(() => {
      const met = step.action!.advanceWhen!(ctxRef.current);
      if (met && !gateOnEnterRef.current) { window.clearInterval(id); next(); }
      if (!met) gateOnEnterRef.current = false; // user undid it → re-arm
    }, 300);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!open || !step) return null;

  const isFirst = index === 0;
  const isLast = index === steps.length - 1;
  const total = steps.length;
  // Gate steps keep the primary button disabled until the real action is done.
  const gateMet = step.action?.advanceWhen ? step.action.advanceWhen(ctx) : true;
  const primaryDisabled = !!step.gate && !gateMet;

  if (step.phase === 'concept') {
    return (
      <ConceptStage
        pose={step.pose}
        title={step.title}
        body={step.body}
        stepIndex={index}
        stepCount={total}
        primaryLabel={step.primaryLabel ?? t('tutorial.continue')}
        primaryDisabled={primaryDisabled}
        onPrimary={() => { void Promise.resolve(step.action?.do?.(ctxRef.current)).then(next); }}
        onBack={isFirst ? null : back}
        onSkip={isLast ? null : () => finish(true)}
        onExit={() => finish(false)}
      />
    );
  }

  const runAssist = step.action?.do
    ? () => { void Promise.resolve(step.action!.do!(ctxRef.current)).then(next); }
    : undefined;

  return (
    <CoachOverlay
      anchor={step.anchor ?? ''}
      placement={step.placement ?? 'bottom'}
      pose={step.pose}
      title={step.title}
      body={step.body}
      hint={step.action?.hint}
      stepIndex={index}
      stepCount={total}
      primaryLabel={step.primaryLabel ?? t('tutorial.next')}
      primaryDisabled={primaryDisabled}
      onPrimary={next}
      onAssist={runAssist}
      assistLabel={step.action?.doLabel}
      onBack={isFirst ? null : back}
      onSkip={step.optional ? next : null}
      onExit={() => finish(false)}
    />
  );
}
